import { debugLog } from "../utils/debugLog.js";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '@clerk/clerk-react';
import Airtable from 'airtable';

export default function IntegrationCard({ name, logoUrl, onInstall, integration }) {
  debugLog('[ENV-FRONTEND] IntegrationCard rendered with integration:', integration);
  debugLog('[ENV-FRONTEND] Integration authConfigId:', integration?.authConfigId);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useUser();
  const userId = user?.primaryEmailAddress?.emailAddress || 'will@getupgraded.ca';

  // Initialize Airtable
  const apiKey = process.env.REACT_APP_AIR_KEY;
  const baseId = process.env.REACT_APP_AIR_BASEID;
  const usersTable = process.env.REACT_APP_AIR_USERS;
  const base = new Airtable({ apiKey }).base(baseId);

  useEffect(() => {
    loadConnectionStatus();
  }, [integration?.key]);

  const loadConnectionStatus = async () => {
    setIsLoading(true);
    try {
      // Check Airtable user record for integration connection status
      const records = await base(usersTable).select({
        filterByFormula: `{Email} = '${userId}'`,
        maxRecords: 1
      }).firstPage();

      if (records.length > 0) {
        const userRecord = records[0];
        const fields = userRecord.fields;
        
        // Check for integration-specific fields
        let isConnected = false;
        
        switch (integration?.key) {
          case 'quickbooks':
            isConnected = fields.Quickbooks && fields.Quickbooks.includes('Connected');
            break;
          case 'hubspot':
            isConnected = fields.Hubspot && fields.Hubspot.includes('Connected');
            break;
          case 'stripe':
            isConnected = fields.Stripe && fields.Stripe.includes('Connected');
            break;
          case 'google-analytics':
            isConnected = fields.GoogleAnalytics && fields.GoogleAnalytics.includes('Connected');
            break;
          default:
            // For integrations without specific fields, check if any integration field exists
            const integrationFields = Object.keys(fields).filter(key => 
              key.toLowerCase().includes(integration?.key?.toLowerCase())
            );
            isConnected = integrationFields.length > 0;
        }
        
        setConnectionStatus(isConnected ? 'connected' : 'not-connected');
      } else {
        setConnectionStatus('not-connected');
      }
    } catch (error) {
      console.error('Error loading connection status from Airtable:', error);
      setConnectionStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'not-connected':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'not-connected':
        return 'Not Connected';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  const getButtonText = () => {
    if (integration?.requiresAuth) {
      return connectionStatus === 'connected' ? 'Connected' : 'Connect';
    }
    return 'Install';
  };

  const getButtonStyle = () => {
    if (integration?.requiresAuth) {
      return connectionStatus === 'connected' 
        ? 'bg-green-600 hover:bg-green-700 text-white' 
        : 'hover:bg-indigo-700 hover:text-white';
    }
    return 'hover:bg-indigo-700 hover:text-white';
  };

  return (
    <div className="flex flex-col items-center bg-white rounded-lg shadow-sm border border-gray-200 p-6 transition hover:shadow-md hover:border-indigo-300 w-full max-w-xs mx-auto">
      <img
        src={logoUrl}
        alt={name + ' logo'}
        className="h-16 w-16 object-contain mb-4 rounded"
        style={{ background: '#f9fafb' }}
        onError={e => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/64?text=Logo'; }}
      />
      <div className="text-lg font-semibold text-gray-900 text-center mb-2">{name}</div>
      
      {integration?.requiresAuth && (
        <div className={`text-xs px-2 py-1 rounded-full border ${getStatusColor()} mb-4`}>
          {isLoading ? 'Loading...' : getStatusText()}
        </div>
      )}
      
      <div className="mt-auto w-full pt-4">
        <button
          onClick={onInstall}
          className={`w-full font-semibold py-2 px-4 rounded-b-lg transition focus:outline-none focus:ring-2 focus:ring-indigo-400 border-t border-b border-l border-r border-gray-200 ${getButtonStyle()}`}
        >
          {getButtonText()}
        </button>
      </div>
    </div>
  );
} 